import { useMemo } from 'react'
import { useSessionStateContext } from '@/features/events/conversation/state'
import type { BlockingBucket, BlockingInboxEntry } from '../types'
import { BLOCKING_BUCKET_ORDER } from '../constants'
import { buildIntent, getPendingBlockingEvent, resolveBlockingBucket } from '../utils'

export interface BlockingBucketsState {
  readonly entriesByBucket: Readonly<Record<BlockingBucket, readonly BlockingInboxEntry[]>>
  readonly counts: Readonly<Record<BlockingBucket, number>>
  readonly hasAny: boolean
}

function createEmptyBuckets(): Record<BlockingBucket, BlockingInboxEntry[]> {
  const entries = BLOCKING_BUCKET_ORDER.map((bucket): [BlockingBucket, BlockingInboxEntry[]] => [
    bucket,
    [],
  ])
  return Object.fromEntries(entries) as Record<BlockingBucket, BlockingInboxEntry[]>
}

export function useBlockingBuckets(): BlockingBucketsState {
  const { sessions } = useSessionStateContext()

  return useMemo(() => {
    const entriesByBucket = createEmptyBuckets()

    try {
      for (const session of sessions) {
        const event = getPendingBlockingEvent(session)
        if (!event) continue

        const bucket = resolveBlockingBucket(event)
        const builtIntent = buildIntent(bucket, event)
        entriesByBucket[bucket].push({
          session,
          bucket,
          event,
          intent: builtIntent.intent,
          ...(builtIntent.toolName ? { toolName: builtIntent.toolName } : {}),
          ...(builtIntent.detail ? { detail: builtIntent.detail } : {}),
        })
      }
    } catch (error) {
      // eslint-disable-next-line no-console -- Intentional: fail-closed derivation failure diagnostics
      console.error('[useBlockingBuckets] failed to derive blocking buckets', error)
      const empty = createEmptyBuckets()
      return {
        entriesByBucket: empty,
        counts: countBuckets(empty),
        hasAny: false,
      }
    }

    const counts = countBuckets(entriesByBucket)
    const hasAny = BLOCKING_BUCKET_ORDER.some((bucket) => counts[bucket] > 0)

    return { entriesByBucket, counts, hasAny }
  }, [sessions])
}

function countBuckets(
  entriesByBucket: Record<BlockingBucket, readonly BlockingInboxEntry[]>,
): Record<BlockingBucket, number> {
  const entries = BLOCKING_BUCKET_ORDER.map((bucket): [BlockingBucket, number] => [
    bucket,
    entriesByBucket[bucket].length,
  ])
  return Object.fromEntries(entries) as Record<BlockingBucket, number>
}
